import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Tooltip, useTheme, alpha } from '@mui/material';

/**
 * Horizontally-scrollable MITRE ATT&CK coverage heatmap. One column per
 * tactic (kill-chain order, as returned by the API), one cell per technique
 * observed in the range. Cell intensity is count / max, blended from the
 * theme's warning color (low) to its error color (high) over the paper
 * background. Clicking a cell jumps to the detections list filtered to that
 * technique.
 */
const CELL_HEIGHT = 46;
const COLUMN_WIDTH = 200;

const cellBackground = (theme, intensity) => {
  if (!intensity) return theme.palette.action.hover;
  const color = intensity > 0.55 ? theme.palette.error.main : theme.palette.warning.main;
  return alpha(color, 0.12 + Math.min(1, intensity) * 0.68);
};

const TechniqueCell = ({ technique, max, onClick }) => {
  const theme = useTheme();
  const intensity = max ? technique.count / max : 0;
  const bg = cellBackground(theme, intensity);
  const textColor = intensity > 0.45 ? theme.palette.getContrastText(bg) : theme.palette.text.primary;

  const tooltip = (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{technique.id} · {technique.name}</Typography>
      <Typography variant="caption" display="block">
        {technique.count} detection{technique.count === 1 ? '' : 's'}
        {technique.critical ? ` · ${technique.critical} critical` : ''}
        {technique.high ? ` · ${technique.high} high` : ''}
      </Typography>
      {technique.subTechniques?.length > 0 && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          Sub-techniques: {technique.subTechniques.join(', ')}
        </Typography>
      )}
      {technique.sample?.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {technique.sample.map((s) => (
            <Typography key={s._id} variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
              • {s.title || s._id}{s.severity ? ` (${s.severity})` : ''}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="right">
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onClick(technique)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(technique); }}
        sx={{
          height: CELL_HEIGHT,
          bgcolor: bg,
          color: textColor,
          borderRadius: 1,
          px: 1,
          py: 0.5,
          mb: 0.5,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden',
          '&:hover': { outline: (t) => `2px solid ${t.palette.primary.main}` },
          '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}` },
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {technique.id}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography variant="caption" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={technique.name}>
            {technique.name}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>{technique.count}</Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

const MitreHeatmap = ({ data }) => {
  const navigate = useNavigate();

  const tactics = data?.tactics || [];
  const detections = data?.detections ?? 0;
  const mapped = data?.mapped ?? 0;
  const techniqueCount = data?.techniques ?? 0;
  const max = data?.max || 0;

  const goToTechnique = (technique) => {
    navigate(`/dashboard/detections?search=${encodeURIComponent(technique.id)}`);
  };

  if (!techniqueCount || !tactics.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Techniques come from EDR alerts and AI triage.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {mapped} of {detections} detection{detections === 1 ? '' : 's'} mapped to {techniqueCount} technique{techniqueCount === 1 ? '' : 's'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
        {tactics.map((tactic) => {
          const total = tactic.techniques.reduce((s, t) => s + (t.count || 0), 0);
          return (
            <Box key={tactic.id} sx={{ flex: `0 0 ${COLUMN_WIDTH}px`, width: COLUMN_WIDTH }}>
              <Box sx={{ mb: 0.75, px: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={tactic.name}>
                  {tactic.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">{total} total</Typography>
              </Box>
              {tactic.techniques.length === 0 ? (
                <Box sx={{ height: CELL_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                  <Typography variant="body2">—</Typography>
                </Box>
              ) : (
                tactic.techniques.map((technique) => (
                  <TechniqueCell key={technique.id} technique={technique} max={max} onClick={goToTechnique} />
                ))
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MitreHeatmap;
